// src/components/GoogleSignInButton.js
// "Continuar com Google". Google vouches for the e-mail, so no verification
// e-mail is needed.
// - Signup page: consents are collected BEFORE the button (it stays disabled
//   until they are given) and onSignedIn creates the profile right away.
// - Login page: returning users just sign in; a first-timer without a profile
//   is routed by useAuth to /complete-signup to give the consents.
import React, { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithCredential } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { rememberSignupCampaign } from '../utils/createProfile';

const GoogleLogo = () => (
    <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

// Staging only: the Auth emulator accepts unsigned Google ID tokens, so
// `?testGoogleEmail=...` signs in without the popup window (which browser
// automation can't drive). Only active in emulator builds; real Firebase
// rejects such tokens anyway.
const emulatedGoogleEmail = () => {
    if (!process.env.REACT_APP_EMULATOR_HOST) return null;
    try { return new URLSearchParams(window.location.search).get('testGoogleEmail'); } catch (e) { return null; }
};
const emulatedGoogleCredential = () => {
    const email = emulatedGoogleEmail();
    const claims = { sub: `emu-${email}`, email, email_verified: true, name: email.split('@')[0] };
    return GoogleAuthProvider.credential(JSON.stringify(claims));
};

export default function GoogleSignInButton({ campaignId = null, label = 'Continuar com Google', disabled = false, onSignedIn = null, beforeSignIn = null, afterSignIn = null, onMfaRequired = null }) {
    const { setModal } = useAuth();
    const [busy, setBusy] = useState(false);

    const handleClick = async () => {
        setBusy(true);
        rememberSignupCampaign(campaignId);
        try {
            if (beforeSignIn) beforeSignIn();
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            const { user } = await (emulatedGoogleEmail()
                ? signInWithCredential(auth, emulatedGoogleCredential())
                : signInWithPopup(auth, provider));
            if (onSignedIn) await onSignedIn(user);
            // useAuth takes over: existing profile → app; new user without
            // consents (login page) → /complete-signup.
        } catch (error) {
            const code = error?.code || '';
            if (code === 'auth/multi-factor-auth-required' && onMfaRequired) {
                onMfaRequired(error); // two-step verification (admins): LoginScreen asks for the code
            } else if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
                // User closed the window: nothing to report.
            } else if (code === 'auth/popup-blocked') {
                setModal({ title: 'Janela bloqueada', message: 'Seu navegador bloqueou a janela do Google. Permita pop-ups para escambo.digital e tente de novo.' });
            } else if (code === 'auth/account-exists-with-different-credential') {
                setModal({ title: 'Conta já existe', message: 'Este e-mail já tem uma conta com senha. Entre com e-mail e senha.' });
            } else if (code === 'auth/unauthorized-domain' || code === 'auth/operation-not-allowed') {
                setModal({ title: 'Login com Google indisponível', message: 'O login com Google ainda não está ativado. Use e-mail e senha por enquanto.' });
            } else {
                console.error('[GoogleSignIn]', error);
                setModal({ title: 'Erro no login com Google', message: 'Não foi possível entrar com o Google. Tente novamente.' });
            }
        } finally {
            if (afterSignIn) afterSignIn();
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={busy || disabled}
            className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <GoogleLogo />
            {busy ? 'Abrindo o Google...' : label}
        </button>
    );
}

export const OrDivider = () => (
    <div className="flex items-center gap-3 my-5" aria-hidden="true">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs uppercase tracking-wide text-gray-400">ou</span>
        <div className="flex-1 h-px bg-gray-200" />
    </div>
);
